import { useState, useEffect } from "react";
import { ConversationEmbed } from "@/components/conversation-renderer/ConversationEmbed";
import {
  TabbedConversation as TabbedConversationBase,
  ConversationTab,
  type TabbedConversationProps,
} from "@/components/conversation-renderer/TabbedConversation";
import type { RenderConversation } from "@/components/conversation-renderer/types";
import { usePreferencesStore } from "@/stores/preferences/preferences-provider";
import { useIsMobile } from "@/hooks/use-mobile";

// App Widget components
import { AppWidget } from "@/components/conversation-renderer/AppWidget";
import {
  AppWidgetList,
  AppWidgetImage,
  AppWidgetTrackList,
  AppWidgetCart,
  AppWidgetConfirmation,
} from "@/components/conversation-renderer/AppWidgetVariants";

export function Conversation({
  conversation,
  height = 500,
}: {
  conversation: RenderConversation;
  height?: number;
}) {
  const theme = usePreferencesStore((s) => s.themeMode);
  const isMobile = useIsMobile();

  return (
    <ConversationEmbed
      conversation={conversation}
      theme={theme}
      view={isMobile ? "mobile" : "desktop"}
      height={height}
    />
  );
}

/**
 * Tabbed conversation wrapper that binds theme from preferences store.
 */
export function TabbedConversation({
  children,
  height = 500,
  ...rest
}: Omit<TabbedConversationProps, "theme" | "view">) {
  const theme = usePreferencesStore((s) => s.themeMode);
  const isMobile = useIsMobile();

  return (
    <TabbedConversationBase
      theme={theme}
      view={isMobile ? "mobile" : "desktop"}
      height={height}
      {...rest}
    >
      {children}
    </TabbedConversationBase>
  );
}

// Re-export ConversationTab for MDX usage
export { ConversationTab };

// Re-export App Widget components for MDX usage
export {
  AppWidget,
  AppWidgetList,
  AppWidgetImage,
  AppWidgetTrackList,
  AppWidgetCart,
  AppWidgetConfirmation,
};

// ============================================================================
// TabbedConversationSSR - SSR-compatible version with path-based loading
// ============================================================================

export interface TabDefinition {
  id: string;
  label: string;
  conversationPath: string;
}

export interface TabbedConversationSSRProps {
  tabs: TabDefinition[];
  height?: number;
  /** Called when conversations are loaded and component is ready */
  onReady?: () => void;
}

/**
 * SSR-compatible tabbed conversation component.
 * Loads conversations from paths at runtime.
 *
 * Usage in MDX:
 * ```mdx
 * <TabbedConversationSSR
 *   height={600}
 *   tabs={[
 *     { id: "spotify", label: "Spotify", conversationPath: "/conversations/mockgpt/spotify.json" },
 *     { id: "instacart", label: "Instacart", conversationPath: "/conversations/mockgpt/instacart.json" }
 *   ]}
 * />
 * ```
 */
export function TabbedConversationSSR({
  tabs,
  height = 500,
  onReady,
}: TabbedConversationSSRProps) {
  const theme = usePreferencesStore((s) => s.themeMode);
  const isMobile = useIsMobile();
  const [conversations, setConversations] = useState<
    Map<string, RenderConversation>
  >(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load all conversations on mount
  useEffect(() => {
    async function loadConversations() {
      try {
        const loaded = new Map<string, RenderConversation>();

        await Promise.all(
          tabs.map(async (tab) => {
            const response = await fetch(tab.conversationPath);
            if (!response.ok) {
              throw new Error(
                `Failed to load ${tab.conversationPath}: ${response.status}`,
              );
            }
            const contentType = response.headers.get("content-type") ?? "";
            const text = await response.text();
            if (
              !contentType.includes("application/json") &&
              text.trimStart().startsWith("<")
            ) {
              throw new Error(
                `Conversation file not found or server returned an error page (expected JSON from ${tab.conversationPath})`,
              );
            }
            const data = JSON.parse(text) as RenderConversation;
            loaded.set(tab.id, data);
          }),
        );

        setConversations(loaded);
        setLoading(false);

        // Signal ready after state update and next frame
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            onReady?.();
          });
        });
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to load conversations",
        );
        setLoading(false);
      }
    }

    loadConversations();
  }, [tabs, onReady]);

  if (error) {
    return (
      <div className="my-8 p-4 border border-red-200 dark:border-red-800 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
        Failed to load conversations: {error}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="my-8">
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 overflow-hidden bg-white dark:bg-zinc-950">
          <div className="flex border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900">
            {tabs.map((tab, i) => (
              <span
                key={tab.id}
                className={`px-4 py-2 text-sm font-medium ${
                  i === 0
                    ? "text-zinc-900 dark:text-zinc-100 border-b-2 border-blue-500 -mb-px bg-white dark:bg-zinc-950"
                    : "text-zinc-500 dark:text-zinc-400"
                }`}
              >
                {tab.label}
              </span>
            ))}
          </div>
          <div
            className="flex items-center justify-center"
            style={{ height: height - 42 }}
          >
            <div className="animate-pulse text-zinc-400 dark:text-zinc-500 text-sm">
              Loading conversations...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="my-8">
      <TabbedConversationBase
        theme={theme}
        view={isMobile ? "mobile" : "desktop"}
        height={height}
      >
        {tabs.map((tab) => {
          const conversation = conversations.get(tab.id);
          if (!conversation) return null;
          return (
            <ConversationTab
              key={tab.id}
              label={tab.label}
              conversation={conversation}
            />
          );
        })}
      </TabbedConversationBase>
    </div>
  );
}
